import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";
import TeamPersonModalForm from "./TeamPersonModalForm";
import TeamPersonsList from "./TeamPersonList";
import { Person, EventDto } from "../Admin/ID/types";

interface FunctionsByEvent {
    eventID: number;
    functions: string[];
}

type PersonWithFunctionsByEvent = Person & {
    functionsByEvent?: FunctionsByEvent[];
    uploadImageBase64?: string;
};

const TeamPerson: React.FC = () => {
    const { eventId, userId } = useParams();

    const apiUrl = process.env.REACT_APP_API_URL!;
    const numericEventId = Number(eventId);
    const numericUserId = Number(userId);

    const [persons, setPersons] = useState<Person[]>([]);
    const [event, setEvent] = useState<EventDto | null>(null);
    const [functions, setFunctions] = useState<{ id: number; name: string }[]>([]);

    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [addingPerson, setAddingPerson] = useState(false);
    const [activeTab, setActiveTab] = useState<"all" | "event">(numericEventId ? "event" : "all");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const normalizePerson = (
        p: any,
        eventFunctions: { id: number; name: string }[]
    ): Person => ({
        ...p,
        userId: p.userId ?? p.user?.id ?? numericUserId,
        events: p.events ?? [],
        functions: (p.functions ?? [])
            .map((f: string) => f.trim())
            .filter((fn: string) => eventFunctions.some((ef) => ef.name === fn)),
        zones: p.zones ?? [],
        personImagePath: p.personImagePath ?? "",
        profileUrl: p.personImagePath ?? "",
        hasProfileImage: p.hasImage ?? !!p.personImagePath,
    });

    useEffect(() => {
        if (!numericEventId || !numericUserId) return;

        const fetchData = async () => {
            setLoading(true);
            setError("");

            try {
                const eventRes = await fetch(`${apiUrl}/api/events/${numericEventId}/light`);
                if (!eventRes.ok) throw new Error("Error fetching Event");
                const eventData = await eventRes.json();
                setEvent(eventData);

                const funcRes = await fetch(`${apiUrl}/api/events/${numericEventId}/functions`);
                if (!funcRes.ok) throw new Error("Error fetching Function");
                const functionsData = await funcRes.json();

                const eventFunctions = functionsData.map((f: any) => ({
                    id: f.functionId,
                    name: f.functionName?.trim() ?? "Unknown Function",
                }));

                setFunctions(eventFunctions);

                const personsRes = await fetch(`${apiUrl}/api/persons/by-user?userId=${numericUserId}`);
                if (!personsRes.ok) throw new Error("Error fetching Persons");

                const personsData = await personsRes.json();
                setPersons(personsData.map((p: any) => normalizePerson(p, eventFunctions)));
            } catch (err) {
                console.error(err);
                setError("Error fetching data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [apiUrl, numericEventId, numericUserId]);

    useEffect(() => {
        setActiveTab(numericEventId ? "event" : "all");
    }, [numericEventId]);

    const handleCreate = async (newPerson: PersonWithFunctionsByEvent) => {
        try {
            const dto = {
                ...newPerson,
                userId: numericUserId,
                events: event ? [event] : [],
                functions: newPerson.functions?.map((f) => f.trim()) ?? [],
                uploadImageBase64: newPerson.uploadImageBase64 ?? "",
                personImagePath: newPerson.personImagePath ?? "",
            };

            const res = await fetch(`${apiUrl}/api/persons`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Create failed:", errorText);
                alert("Error Creating Person");
                return;
            }

            const created = normalizePerson(await res.json(), functions);
            setPersons((prev) => [...prev, created]);
            setAddingPerson(false);
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdate = async (person: PersonWithFunctionsByEvent) => {
        try {
            const dto = {
                ...person,
                events: person.events ?? [],
                functions: [
                    ...new Set(
                        person.functionsByEvent?.flatMap((x) => x.functions).map((f) => f.trim()) ?? []
                    ),
                ],
                uploadImageBase64: person.uploadImageBase64 ?? "",
                personImagePath: person.personImagePath ?? "",
            };

            const res = await fetch(`${apiUrl}/api/persons/${person.personId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Update failed:", errorText);
                alert("Error Updating Person");
                return;
            }

            const updated = normalizePerson(await res.json(), functions);
            setPersons((prev) => prev.map((p) => (p.personId === updated.personId ? updated : p)));
            setSelectedPerson(null);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete Person?")) return;
        try {
            const res = await fetch(`${apiUrl}/api/persons/${id}`, { method: "DELETE" });
            if (!res.ok) {
                alert("Error Deleting Person");
                return;
            }
            setPersons((prev) => prev.filter((p) => p.personId !== id));
        } catch (err) {
            console.error(err);
        }
    };

    const userPersons = persons.filter((p) => p.userId === numericUserId);
    const userEventPersons = userPersons.filter((p) => p.events?.some((e) => e.eventID === numericEventId));

    if (!numericEventId || !numericUserId) return <div style={{ padding: 20 }}>No EventID or UserID</div>;
    if (loading) return <div style={{ padding: 20 }}>Loading...</div>;
    if (error) return <div style={{ padding: 20, color: "red" }}>{error}</div>;

    return (
        <>
            <HeroSectionV2
                titleEn="Team Accreditations"
                titleDa="Team Akkrediteringer"
                descriptionEn={event ? `Event: ${event.eventName}` : "Loading..."}
                descriptionDa={event ? `Event: ${event.eventName}` : "Indlæser..."}
                imageSrc={Billede1}
                badgeText="TEAM"
                logoSrc={event?.eventLogoPath ?? ""}
            />

            <div style={{ padding: 20 }}>
                <h2>Team Persons - {event?.eventName}</h2>

                <div className="tabs" style={{ marginBottom: 20, display: "flex", gap: 10 }}>
                    <button
                        onClick={() => setActiveTab("all")}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            backgroundColor: activeTab === "all" ? "#007bff" : "#f5f5f5",
                            color: activeTab === "all" ? "#fff" : "#000",
                            cursor: "pointer",
                        }}
                    >
                        My Persons
                    </button>
                    <button
                        onClick={() => setActiveTab("event")}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            backgroundColor: activeTab === "event" ? "#007bff" : "#f5f5f5",
                            color: activeTab === "event" ? "#fff" : "#000",
                            cursor: "pointer",
                        }}
                    >
                        My Persons for this event
                    </button>
                </div>

                <div style={{ marginBottom: 20 }}>
                    <button className="btn btn--new" onClick={() => setAddingPerson(true)}>
                        Add a Person to Team
                    </button>
                </div>

                {addingPerson && (
                    <TeamPersonModalForm
                        initialPerson={null}
                        onSave={handleCreate}
                        onCancel={() => setAddingPerson(false)}
                        allEvents={event ? [event] : []}
                        allFunctions={functions}
                        currentUserId={numericUserId}
                        currentEventID={numericEventId}
                    />
                )}

                {selectedPerson && (
                    <TeamPersonModalForm
                        initialPerson={selectedPerson}
                        onSave={handleUpdate}
                        onCancel={() => setSelectedPerson(null)}
                        allEvents={event ? [event] : []}
                        allFunctions={functions}
                        currentUserId={numericUserId}
                        currentEventID={numericEventId}
                    />
                )}

                {activeTab === "all" ? (
                    <TeamPersonsList
                        persons={userPersons}
                        onEdit={(id) => setSelectedPerson(persons.find((p) => p.personId === id) || null)}
                        onDelete={handleDelete}
                        tableId="team-persons-all"
                        currentEventId={numericEventId}
                    />
                ) : (
                    <TeamPersonsList
                        persons={userEventPersons}
                        onEdit={(id) => setSelectedPerson(persons.find((p) => p.personId === id) || null)}
                        onDelete={handleDelete}
                        tableId="team-persons-event"
                        currentEventId={numericEventId}
                    />
                )}
            </div>
        </>
    );
};

export default TeamPerson;